/* eslint-disable camelcase */
import { useReducer } from 'react';
import { v4 } from 'uuid';

import { ACTIONS } from 'hb-react/shared/apps/Events/constants/actions';
import type { Guest } from 'hb-react/types';

import type {
  GuestAction,
  GuestState,
  UseGuestsReducerProps,
  UseGuestsReducerReturn,
} from './types';

const transformGuest = ({
  mobile_phone,
  company_name,
  email,
  firstname,
  lastname,
  guest_id,
  id,
}: Guest): Guest => {
  const guestId = guest_id || id || v4();

  return {
    mobile_phone,
    company_name,
    email,
    firstname,
    lastname,
    guest_id: guestId,
    id: id || guestId,
  };
};

const findGuestIndex = (state: GuestState, guestId: string): number =>
  state.findIndex(({ guest_id }) => guest_id === guestId);

const findGuest = (state: GuestState, guestId: string): Guest | undefined =>
  state.find(({ guest_id }) => guest_id === guestId);

const createGuest = (state: GuestState, guest: Guest): GuestState => [
  ...state,
  transformGuest(guest),
];

const deleteGuest = (state: GuestState, guest: Guest): GuestState =>
  state.filter(({ guest_id }) => guest_id !== guest.guest_id);

const updateGuest = (state: GuestState, guest: Guest): GuestState => {
  const index = findGuestIndex(state, guest.guest_id);

  if (index === -1) {
    return state;
  }

  return [
    ...state.slice(0, index),
    transformGuest({ ...state[index], ...guest }),
    ...state.slice(index + 1),
  ];
};

const saveGuests = (state: GuestState, guests: Array<Guest>): GuestState =>
  guests.map((guest) => {
    const existingGuest = findGuest(state, guest.guest_id);

    return transformGuest({ ...existingGuest, ...guest });
  });

const reducer = (state: GuestState, action: GuestAction): GuestState => {
  const { type, payload } = action;

  switch (type) {
    case ACTIONS.CREATE: {
      if (!payload.guest) {
        throw new Error('Guest is required for CREATE action');
      }

      return createGuest(state, payload.guest);
    }

    case ACTIONS.DELETE: {
      if (!payload.guest) {
        throw new Error('Guest is required for DELETE action');
      }

      return deleteGuest(state, payload.guest);
    }

    case ACTIONS.UPDATE: {
      if (!payload.guest) {
        throw new Error('Guest is required for UPDATE action');
      }

      return updateGuest(state, payload.guest);
    }

    case ACTIONS.SAVE: {
      const guests = payload.guests || [];

      return saveGuests(state, guests);
    }

    default:
      throw new Error(`Unsupported action type: ${type}`);
  }
};

export const useGuestsReducer = ({
  currentUser,
}: UseGuestsReducerProps): UseGuestsReducerReturn => {
  const initialState = (): GuestState =>
    (currentUser?.event_guests?.guests || []).map((guest) =>
      transformGuest({ ...guest, guest_id: guest?.id || v4() }),
    );

  const [state, dispatch] = useReducer(reducer, [], initialState);

  return {
    guests: state,
    dispatch,
  };
};
